import { DSBadge, DSDivider, DSIcon, DSSpacer, DSText as DSTextLegacy } from '@zoe/ds-web'
import { DSText } from '@zoe/ds-web/next-gen'
import { memo, useCallback, useRef, useState } from 'react'
import { Button, Dialog, DialogTrigger, Popover } from 'react-aria-components'
import styled, { css } from 'styled-components'

import { DSLinkText } from '@/components/ds/LinkText/LinkText'

export type NavLink = {
  text: string
  href: string
  onPress?: () => void
  badge?: string
  textWeight?: 'light' | 'regular' | 'semiBold'
  // Opt out of the active state — e.g. when two items share an href
  // (ZOE App and Gut Health Test both link to /buymembership).
  disableActive?: boolean
}

// Suppress the browser default focus ring (which shows on mouse/press) and only
// show a clean outline for keyboard focus via react-aria's data-focus-visible.
export const focusVisible = css`
  outline: none;

  &[data-focus-visible] {
    outline: 2px solid var(--color-text-primary);
    outline-offset: 2px;
    border-radius: var(--radius-4);
  }
`

// Disable transitions/animations for users who prefer reduced motion.
export const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    animation: none;
  }
`

const StyledTriggerButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: var(--grid-8);
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  ${focusVisible}
`

const StyledPopover = styled(Popover)`
  z-index: 10;
  min-width: 240px;
  background: var(--color-surface-white);
  border-radius: var(--radius-16);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  outline: none;

  /* Subtle fade so the popover doesn't pop in/out jarringly. */
  transition: opacity 0.15s ease;
  &[data-entering] {
    opacity: 0;
  }
  &[data-exiting] {
    opacity: 0;
  }
  ${reducedMotion}
`

// react-aria focuses the Dialog when the popover opens; suppress its focus ring
// (the panel itself isn't an actionable target). Keyboard focus is shown on the
// individual links via the focus-visible style below.
const StyledDialog = styled(Dialog)`
  outline: none;
`

const StyledPopoverNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: var(--grid-16);
  padding: var(--grid-16) var(--grid-24);

  a {
    display: inline-flex;
    align-items: center;
    outline: none;
  }
  a span {
    color: var(--color-text-link-navigation-bold);
    transition: color 0.2s ease-in-out;
  }
  a[data-hovered] span {
    color: var(--color-text-link-navigation-bold-hover);
  }
  a[data-focus-visible] {
    outline: 2px solid var(--color-text-primary);
    outline-offset: 2px;
    border-radius: var(--radius-4);
  }
`

const StyledAccordionTrigger = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  ${focusVisible}
`

const StyledAccordionPanel = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--grid-24);
  overflow: hidden;
  padding-left: var(--grid-16);
  /* Slide open/closed via max-height; visibility removes the collapsed links
     from the tab order / a11y tree (a plain [hidden] attr is overridden by the
     display:flex above). */
  max-height: ${({ $open }) => ($open ? '60vh' : '0')};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  padding-top: ${({ $open }) => ($open ? 'var(--grid-24)' : '0')};
  padding-bottom: ${({ $open }) => ($open ? 'var(--grid-8)' : '0')};
  transition:
    max-height 0.3s ease,
    padding 0.3s ease,
    visibility 0.3s;
  ${reducedMotion}

  /* Keep each item's text + badge on one line. */
  a {
    display: inline-flex;
    align-items: center;
  }
`

type ProductsMenuProps = {
  label: string
  items: NavLink[]
  isActive: (href: string) => boolean
  defaultOpen?: boolean
  onOpenTrack?: () => void
}

// `narrow` renders the mobile typography (Paragraph/L-Hero via the next-gen DS
// text); the desktop popover keeps the legacy paragraph variant.
const ProductItem = memo(function ProductItem({
  item,
  isActive,
  narrow,
}: {
  item: NavLink
  isActive: (href: string) => boolean
  narrow?: boolean
}) {
  const isCurrent = isActive(item.href) && !item.disableActive
  return (
    <DSLinkText
      href={item.href}
      colourScheme="navigation"
      onPress={item.onPress}
      aria-current={isCurrent ? 'page' : undefined}
    >
      {narrow ? (
        <DSText as="span" variant="paragraphLHero" color="default">
          {item.text}
        </DSText>
      ) : (
        <DSTextLegacy as="span" variant="fluid-paragraph-300" weight="light" color="primary">
          {item.text}
        </DSTextLegacy>
      )}
      {item.badge && (
        <>
          <DSSpacer direction="horizontal" size={12} />
          <DSBadge surfaceColor="yellow-bold" textColor="primary" size="regular">
            {item.badge}
          </DSBadge>
        </>
      )}
    </DSLinkText>
  )
})

// Tracks the "menu opened" event at most once per open (reset when it closes).
const useTrackOpenOnce = (onOpenTrack?: () => void) => {
  const hasTracked = useRef(false)
  const trackOpen = useCallback(() => {
    if (onOpenTrack && !hasTracked.current) {
      onOpenTrack()
      hasTracked.current = true
    }
  }, [onOpenTrack])
  const resetTracked = useCallback(() => {
    hasTracked.current = false
  }, [])
  return { trackOpen, resetTracked }
}

// Two distinct interaction patterns rendered into breakpoint-gated containers
// (see Navigation.tsx). Named by pattern — not device — so we don't bake
// mobile/desktop assumptions into the components.
export const ProductsDropdown = ({
  label,
  items,
  isActive,
  onOpenTrack,
}: Pick<ProductsMenuProps, 'label' | 'items' | 'isActive' | 'onOpenTrack'>) => {
  const [open, setOpen] = useState(false)
  const { trackOpen, resetTracked } = useTrackOpenOnce(onOpenTrack)

  // Press (click/keyboard) drives open/close via DialogTrigger's onOpenChange.
  // The menu deliberately does NOT open on hover: a hover-opened menu was being
  // toggled shut by the same click the user made to open it, reading as a
  // flicker. Click-to-open removes that conflict.
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen)
      if (isOpen) {
        trackOpen()
      } else {
        resetTracked()
      }
    },
    [trackOpen, resetTracked],
  )

  return (
    <DialogTrigger isOpen={open} onOpenChange={handleOpenChange}>
      <StyledTriggerButton>
        <DSTextLegacy as="span" variant="fluid-paragraph-300" weight="light" color="primary" align="center">
          {label}
        </DSTextLegacy>
        <DSIcon name={open ? 'chevron-up' : 'chevron-down'} size={16} aria-hidden="true" />
      </StyledTriggerButton>
      <StyledPopover placement="bottom left" offset={12}>
        <StyledDialog aria-label={label}>
          {/* Close on selection: the header stays mounted across client-side
              navigation, so without this the popover lingers over the new page. */}
          <StyledPopoverNav onClick={() => handleOpenChange(false)}>
            {items.map((item) => (
              <ProductItem key={item.text} item={item} isActive={isActive} />
            ))}
          </StyledPopoverNav>
        </StyledDialog>
      </StyledPopover>
    </DialogTrigger>
  )
}

export const ProductsAccordion = ({
  label,
  items,
  isActive,
  defaultOpen = false,
  onOpenTrack,
}: Pick<ProductsMenuProps, 'label' | 'items' | 'isActive' | 'defaultOpen' | 'onOpenTrack'>) => {
  const [open, setOpen] = useState(defaultOpen)
  const { trackOpen, resetTracked } = useTrackOpenOnce(onOpenTrack)

  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev
      if (next) {
        trackOpen()
      } else {
        resetTracked()
      }
      return next
    })
  }, [trackOpen, resetTracked])

  return (
    <div>
      <StyledAccordionTrigger aria-expanded={open} aria-controls="mobile-products-panel" onPress={handleToggle}>
        <DSText as="span" variant="paragraphLHero" color="default" isBold={open}>
          {label}
        </DSText>
        <DSIcon name={open ? 'chevron-up' : 'chevron-down'} size={16} aria-hidden="true" />
      </StyledAccordionTrigger>
      <StyledAccordionPanel id="mobile-products-panel" $open={open}>
        {items.map((item) => (
          <ProductItem key={item.text} item={item} isActive={isActive} narrow />
        ))}
      </StyledAccordionPanel>
      <DSDivider spacing={24} />
    </div>
  )
}
